import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Color, FontSize } from '../../constants/style-constants';
import { breakpoint } from '../../projlibs/breakpoints';
import { DashboardTableTitle, StyledText } from '../Shared/StyledText';
import { FlexTableController } from '../Shared/FlexTableController';
import { PageController } from '../Shared/PageController';
import { PERIOD, PAYMENT, PAID_BY, BALANCE } from '../../constants/table-constants';
import { LoadingIndicator } from '../LoadingIndicator/LoadingIndicator';
import { breakpoint_sizes } from '../../projlibs/breakpoints';
export const CardWrapper = styled.div`
	background-color:${Color.white};
	height: 100%;
	min-height: 617px;
	box-sizing:border-box;
	border: 1px solid #E8EAEA;
	border-radius: 8px;
	${props => css`
		width:${props.width};
		padding: ${props.padding};
		margin-bottom: ${props.marginBottom};
	`}
	${breakpoint('medium down')} {
		width:100%;
	}
`;

const TableTitleWrapper = styled.div`
	display: flex;
	justify-content: space-between;;
	height: 48px;
	display: flex;
	justify-content: flex-start;
	flex-direction: column;
	gap: 8px;
	padding: 32px 32px 16px 32px;
	margin-bottom: 16px;
`;

export const DashboardCard = props => {
	const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < breakpoint_sizes.large);
	
	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < breakpoint_sizes.large);
		};
		
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);
	return (
		<CardWrapper width='70%' className='card-wrapper'>
			<TableTitleWrapper>
				<DashboardTableTitle>Payment History</DashboardTableTitle>
				{props.loading ? (
					<LoadingIndicator loading={props.loading} />
				) : (
					<StyledText 
					display='block'
					marginLeft='auto'
					fontWeight= 'normal' 
					fontSize={'16px'} 
					horizontalAlign='right'
					color={'#152828'}
				>Page {props.page} of {props.totalPages}
				</StyledText>
				)}
			</TableTitleWrapper>
			<FlexTableController tableData={SetupTableData(props.paymentData, isMobile)} disableMobileStack={true} />
			<PageController pageNumber={props.page} setPageNumber={props.setPage} numPages={props.totalPages} />
		</CardWrapper>

	);
};

const SetupTableData = (paymentHistory, isMobile) => {
	if (!paymentHistory || !paymentHistory.DuesPayments) {
		return {
			titles: isMobile ? [PERIOD, PAYMENT, BALANCE] : [PERIOD, PAYMENT, PAID_BY, BALANCE],
			rows: []
		};
	}

	const duesPayments = paymentHistory.DuesPayments;
	const rows = [];
	
	// Group payments by year
	const groupedByYear = duesPayments.reduce((acc, payment) => {
		const year = payment.Year;
		if (!acc[year]) {
			acc[year] = [];
		}
		acc[year].push(payment);
		return acc;
	}, {});

	// Sort years in descending order (most recent first)
	const sortedYears = Object.keys(groupedByYear).sort((a, b) => parseInt(b) - parseInt(a));

	sortedYears.forEach(year => {
		// Add year header row
		const yearHeaderRow = [
			{
				element: (
					<StyledText
						color="#162D4C"
						fontSize="14px"
						fontWeight="600"
					>
					{year}
					</StyledText>
				),
				doesGrow: true,
				className: 'year-header-row'
			},
			{
				element: (
					<StyledText
						color="#162D4C"
						fontSize="14px"
						fontWeight="normal"
						whiteSpace="nowrap"
					>
						{paymentHistory.DuesYears?.find(d => d.Year === parseInt(year))?.YearlyDuesLabel || ''}
					</StyledText>
				),
				doesGrow: true,
				className: 'year-header-row'
			}
		];
		
		// Add PAID_BY column placeholder on desktop only
		if (!isMobile) {
			yearHeaderRow.push({
				element: (
					<StyledText
						color="#162D4C"
						fontSize="14px"
						fontWeight="600"
					>
						
					</StyledText>
				),
				doesGrow: true,
				className: 'year-header-row'
			});
		}
		
		// Add BALANCE column placeholder
		yearHeaderRow.push({
			element: (
				<StyledText
					color="#162D4C"
					fontSize="14px"
					fontWeight="600"
				>
					
				</StyledText>
			),
			doesGrow: true,
			className: 'year-header-row'
		});
		
		rows.push(yearHeaderRow);

		// Add payment rows for this year
		const yearPayments = groupedByYear[year];
		yearPayments.forEach((payment) => {
			const paymentRow = [
				{
					element: (
						<StyledText
							color="#152828"
							fontWeight="normal"
							fontSize={FontSize.body1}>
							{payment.PeriodLabel}
						</StyledText>
					),
					sortValue: payment.Period
				},
				{
					element: (
						<div>
							{/* Desktop view - just payment amount */}
							<StyledText
								color={"#001414"}
								fontWeight="600"
								fontSize={FontSize.body1}
								style={{ display: 'block' }}
								className="desktop-payment">
								${payment.PaymentAmount.toFixed(2)} CAD
							</StyledText>
							{/* Mobile view - payment amount with paid by */}
							<div className="mobile-payment" style={{ display: 'none' }}>
								<StyledText
									color={"#001414"}
									fontWeight="600"
									fontSize={FontSize.body1}>
									${payment.PaymentAmount.toFixed(2)} CAD
								</StyledText>
								{payment.PaidByLabel && (
									<div>
										<StyledText
											color={"#152828"}
											fontWeight="normal"
											whiteSpace="nowrap"
											fontSize="14px">
											Paid by {payment.PaidByLabel}
										</StyledText>
									</div>
								)}
							</div>
						</div>
					),
					sortValue: payment.PaymentAmount
				}
			];
			
			// Add PAID_BY column on desktop only
			if (!isMobile) {
				paymentRow.push({
					element: (
						<div className="paid-by-column">
							{/* Desktop view - paid by */}
							<StyledText
								color={"#152828"}
								fontWeight="normal"
								fontSize={FontSize.body1}
								className="desktop-paid-by">
								{payment.PaidByLabel || '-'}
							</StyledText>
						</div>
					),
					sortValue: payment.PaidByLabel,
					className: 'paid-by-column'
				});
			}
			
			// Add BALANCE column
			paymentRow.push({
				element: (
					<StyledText
						color={
							payment.BalanceAmount > 0 ? '#9E2506' : 
							payment.BalanceAmount === 0 ? '#001414' : 
							'#0D812E'
						}
						fontSize={FontSize.body1}>
						${payment.BalanceAmount.toFixed(2)}
					</StyledText>
				),
				sortValue: payment.BalanceAmount
			});
			
			rows.push(paymentRow);
		});
	});

	// Only include PAID_BY column on desktop (medium screens and up)
	const titles = isMobile 
		? [PERIOD, PAYMENT, BALANCE]
		: [PERIOD, PAYMENT, PAID_BY, BALANCE];
	
	return {
		titles: titles,
		rows: rows
	};
};